/**
 * CR500 Phase 4A: MetaPillsBlock
 * 
 * Renders the deal type badge (BONUS/DISCOUNT/POINTS/etc.) + status pill (Active/Expired/Upcoming).
 * This mirrors the V1 DealGiftCard row containing DealTypeBadge + DealStatusBadge.
 */

import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal, DealCategory } from "@/lib/types/deals";
import { DealTypeBadge } from "@/components/deals/DealTypeBadge";
import { DealStatusBadge, computeDealStatus, type DealStatus } from "@/components/deals/DealStatusBadge";
import { AvailabilityScopeBadge } from "@/components/deals/AvailabilityScopeBadge";
import { getDealType } from "@/lib/deals/getDealType";
import { cn } from "@/lib/utils";

/**
 * MetaPillsBlock renders the deal category and status pills.
 * 
 * Visibility: Always renders when block is enabled (controlled at spec level).
 * 
 * Behavior (parity with V1):
 * - DealTypeBadge: Shows BONUS/DISCOUNT/POINTS/IN_KIND/UNCLASSIFIED
 * - DealStatusBadge: Shows Active/Expired/Upcoming based on validity dates
 * - AvailabilityScopeBadge: Shows regional scope if available
 */
export function MetaPillsBlock({ deal, block, context }: DealCardBlockProps<Deal>) {
  // Derive deal category using the same helper as V1
  const { category: dealCategory } = getDealType(deal);
  
  // CR432: Use unified helper via deal object (timezone-safe, unknown-aware)
  const dealStatus: DealStatus = computeDealStatus(deal as any);
  
  // Debug mode indicator
  const isDebugMode = typeof window !== "undefined" && 
    new URLSearchParams(window.location.search).get("dealCardV2debug") === "1";
  
  return (
    <div 
      className={cn(
        "flex items-center flex-wrap gap-1.5 relative z-20 flex-shrink-0",
        isDebugMode && "ring-1 ring-amber-400/30"
      )}
      data-block-id={block.id}
      data-block-type={block.type}
    >
      {/* Deal Type Badge - Bonus/Discount/Points/etc. */}
      <DealTypeBadge category={dealCategory as DealCategory} size="sm" />
      
      {/* Deal Status Badge - Active/Expired/Upcoming */}
      <DealStatusBadge status={dealStatus} size="sm" />
      
      {/* Availability Scope Badge - Regional scope if present (parity with V1) */}
      {deal.availability_scope && (
        <AvailabilityScopeBadge 
          scope={deal.availability_scope} 
          notes={deal.availability_notes}
          showUnknown={false}
        />
      )}
      
      {/* CR412: Claim channel indicator */}
      {(deal as any).claim_channel && (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground border border-border">
          Claim via {(deal as any).claim_channel.charAt(0).toUpperCase() + (deal as any).claim_channel.slice(1)}
        </span>
      )}
      
      {/* Debug label overlay */}
      {isDebugMode && (
        <span className="absolute -top-4 left-0 text-[9px] bg-amber-100 text-amber-700 px-1 rounded">
          meta_pills
        </span>
      )}
    </div>
  );
}

export default MetaPillsBlock;
